import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Bell, BellOff, Pencil, Trash2, Play, Pause, TrendingUp, TrendingDown, CheckCircle2, Repeat } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function AlertRow({ alert, onPauseToggle, onEdit, onDelete }) {
  const above = alert.condition === "above";
  return (
    <div
      data-testid={`alert-row-${alert.id}`}
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3 ${alert.paused ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Link
          to={`/asset/${alert.symbol}`}
          className="text-sm font-bold text-white hover:text-[#00E0FF] transition-colors mono"
          data-testid={`alert-symbol-link-${alert.id}`}
        >
          {alert.symbol}
        </Link>
        <Badge className={`text-[10px] ${above ? "bg-[#00FFA3]/15 text-[#00FFA3]" : "bg-[#FF3B30]/15 text-[#FF3B30]"} border-0`}>
          {above ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {above ? "Above" : "Below"} ${alert.target_price}
        </Badge>
        {alert.repeat && (
          <Badge className="text-[10px] bg-[#00E0FF]/15 text-[#00E0FF] border-0" data-testid={`alert-repeat-badge-${alert.id}`}>
            <Repeat className="w-3 h-3 mr-1" />
            Repeat{alert.trigger_count > 0 ? ` · fired ${alert.trigger_count}×` : ""}
          </Badge>
        )}
        {alert.paused && (
          <Badge className="text-[10px] bg-gray-500/15 text-gray-400 border-0">
            <Pause className="w-3 h-3 mr-1" /> Paused
          </Badge>
        )}
        {alert.triggered && (
          <Badge className="text-[10px] bg-[#F59E0B]/15 text-[#F59E0B] border-0">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Triggered
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-gray-500 mono mr-2">
          {alert.triggered
            ? `hit at $${alert.current_price}`
            : `last $${alert.current_price}`}
        </span>
        {!alert.triggered && (
          <>
            <Button
              data-testid={`alert-pause-btn-${alert.id}`}
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/5"
              title={alert.paused ? "Resume alert" : "Pause alert"}
              onClick={() => onPauseToggle(alert)}
            >
              {alert.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </Button>
            <Button
              data-testid={`alert-edit-btn-${alert.id}`}
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-gray-400 hover:text-[#00E0FF] hover:bg-white/5"
              title="Edit alert"
              onClick={() => onEdit(alert)}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        <Button
          data-testid={`alert-delete-btn-${alert.id}`}
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-gray-400 hover:text-[#FF3B30] hover:bg-white/5"
          title="Delete alert"
          onClick={() => onDelete(alert)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function AlertsHub() {
  const navigate = useNavigate();
  const { isAuthenticated, authHeaders } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editCondition, setEditCondition] = useState("above");
  const [editRepeat, setEditRepeat] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/alerts`, { headers: authHeaders() });
      setAlerts(data.alerts);
    } catch {
      toast.error("Could not load alerts");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isAuthenticated) fetchAlerts();
    else setLoading(false);
  }, [isAuthenticated, fetchAlerts]);

  const pauseToggle = async (alert) => {
    try {
      await axios.put(`${API}/alerts/${alert.id}`, { paused: !alert.paused }, { headers: authHeaders() });
      toast.success(alert.paused ? "Alert resumed" : "Alert paused");
      fetchAlerts();
    } catch {
      toast.error("Could not update alert");
    }
  };

  const deleteAlert = async (alert) => {
    try {
      await axios.delete(`${API}/alerts/${alert.id}`, { headers: authHeaders() });
      toast.success("Alert deleted");
      fetchAlerts();
    } catch {
      toast.error("Could not delete alert");
    }
  };

  const openEdit = (alert) => {
    setEditing(alert);
    setEditPrice(String(alert.target_price));
    setEditCondition(alert.condition);
    setEditRepeat(!!alert.repeat);
  };

  const saveEdit = async () => {
    const price = parseFloat(editPrice);
    if (!price || price <= 0) {
      toast.error("Enter a valid target price");
      return;
    }
    try {
      await axios.put(
        `${API}/alerts/${editing.id}`,
        { target_price: price, condition: editCondition, repeat: editRepeat },
        { headers: authHeaders() }
      );
      toast.success("Alert updated");
      setEditing(null);
      fetchAlerts();
    } catch {
      toast.error("Could not update alert");
    }
  };

  const active = alerts.filter((a) => !a.triggered);
  const triggered = alerts.filter((a) => a.triggered);

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            data-testid="alerts-back-btn"
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "Unbounded" }}>
              ALERTS HUB
            </h1>
          </div>
          <p className="text-gray-400">All your price alerts in one place — edit, pause, or clear them out</p>
        </motion.div>

        {!isAuthenticated ? (
          <Card className="glass-effect border border-white/10">
            <CardContent className="p-8 text-center" data-testid="alerts-signin-prompt">
              <p className="text-gray-400">Sign in from the dashboard to manage your price alerts.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-effect border border-white/10">
            <CardContent className="p-6">
              <Tabs defaultValue="active">
                <TabsList className="bg-white/5 mb-4">
                  <TabsTrigger value="active" data-testid="alerts-tab-active">
                    Active ({active.length})
                  </TabsTrigger>
                  <TabsTrigger value="triggered" data-testid="alerts-tab-triggered">
                    Triggered history ({triggered.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="space-y-2" data-testid="alerts-active-list">
                  {loading ? (
                    <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
                  ) : active.length === 0 ? (
                    <div className="text-center py-10" data-testid="alerts-empty-state">
                      <BellOff className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No active alerts yet.</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Open any asset from the dashboard and hit "Set Alert" to create one.
                      </p>
                    </div>
                  ) : (
                    active.map((a) => (
                      <AlertRow key={a.id} alert={a} onPauseToggle={pauseToggle} onEdit={openEdit} onDelete={deleteAlert} />
                    ))
                  )}
                </TabsContent>

                <TabsContent value="triggered" className="space-y-2" data-testid="alerts-triggered-list">
                  {triggered.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">No alerts have triggered yet.</p>
                  ) : (
                    triggered.map((a) => (
                      <AlertRow key={a.id} alert={a} onPauseToggle={pauseToggle} onEdit={openEdit} onDelete={deleteAlert} />
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[#0b1020] border border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit {editing?.symbol} alert</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Change the target price, direction, or repeat behavior
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Button
                data-testid="edit-condition-above"
                size="sm"
                variant={editCondition === "above" ? "default" : "outline"}
                className={editCondition === "above" ? "bg-[#00FFA3] text-black hover:bg-[#00FFA3]/90" : "border-white/10 text-gray-400"}
                onClick={() => setEditCondition("above")}
              >
                <TrendingUp className="w-3.5 h-3.5 mr-1" /> Above
              </Button>
              <Button
                data-testid="edit-condition-below"
                size="sm"
                variant={editCondition === "below" ? "default" : "outline"}
                className={editCondition === "below" ? "bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90" : "border-white/10 text-gray-400"}
                onClick={() => setEditCondition("below")}
              >
                <TrendingDown className="w-3.5 h-3.5 mr-1" /> Below
              </Button>
            </div>
            <Input
              data-testid="edit-target-price-input"
              type="number"
              step="any"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              placeholder="Target price"
              className="bg-white/5 border-white/10 text-white"
            />
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Repeat className="w-3.5 h-3.5 text-[#00E0FF]" />
                <div>
                  <div className="text-xs font-semibold text-white">Repeat alert</div>
                  <div className="text-[10px] text-gray-500">Re-arms after each trigger — notifies on every cross</div>
                </div>
              </div>
              <Switch data-testid="edit-repeat-switch" checked={editRepeat} onCheckedChange={setEditRepeat} />
            </div>
            <Button
              data-testid="edit-alert-save-btn"
              onClick={saveEdit}
              className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold"
            >
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
